/**
 * Hook - useDeposit
 *
 * Hook pour effectuer un versement d'espèces
 */

import { useState } from 'react';
import { useDependencies } from '../contexts/DependencyContext';
import { ProcessDepositUseCase } from '../../application/use-cases/deposit/ProcessDepositUseCase';
import { DepositRequestDTO } from '../../application/dto/DepositRequestDTO';
import { TransactionResponseDTO } from '../../application/dto/TransactionResponseDTO';
import { Account } from '../../domain/entities/Account';

export function useDeposit() {
  const {
    transactionRepository,
    accountRepository,
    cashRegisterRepository,
    auditLogger,
    depositGateway,
  } = useDependencies();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TransactionResponseDTO | null>(null);

  /**
   * Exécute un versement.
   * @param request - données du versement
   * @param resolvedAccount - compte déjà vérifié via CBS (optionnel)
   */
  const executeDeposit = async (
    request: DepositRequestDTO,
    resolvedAccount?: Account
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const useCase = new ProcessDepositUseCase(
        transactionRepository,
        accountRepository,
        cashRegisterRepository,
        auditLogger,
        depositGateway
      );

      const depositResult = await useCase.execute(request, resolvedAccount);

      console.log('🟣 [useDeposit] depositResult:', {
        success: depositResult.success,
        hasValue: 'value' in depositResult,
        value: depositResult.success ? depositResult.value : null
      });

      if (depositResult.success) {
        console.log('🟣 [useDeposit] Setting result:', depositResult.value);
        setResult(depositResult.value);
        return true;
      } else {
        setError(depositResult.error.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError(null);
    setResult(null);
  };

  return {
    executeDeposit,
    loading,
    error,
    result,
    reset,
  };
}
